/**
 * Counts what actually happens when a finger lands on the annotation overlay.
 *
 * Reported 2026-09-06 from a handset: the Rect tool highlights, the page does
 * not scroll under the finger, the reporter's presence label reaches the
 * desktop while they drag -- and no mark is ever drawn. Three plausible
 * mechanisms fit all of that, and no amount of reading the code separates
 * them, so count each one on the device instead.
 *
 * ⚠️ This does NOT reproduce under emulation. 19-mobile-touch-annotation
 * passes at a 390x844 phone viewport with hasTouch/isMobile and again at
 * deviceScaleFactor 3, which rules out "Fabric ignores touch" outright. The
 * remaining candidates are specific to the real Teams Android WebView, and
 * only the handset can tell them apart:
 *
 *   domTouch > 0, fabricDown = 0  Touch reaches the DOM, Fabric never sees it.
 *                                 Its listeners are on a stale or orphaned
 *                                 node, or that WebView is not delivering the
 *                                 pointer events Fabric v6 binds -- the
 *                                 pitfall-19a shape.
 *   fabricDown > 0, objects = 0   Fabric sees the press but no tool handler is
 *                                 attached, so setTool is the place to look.
 *   domTouch = 0                  The gesture never reaches the overlay at all,
 *                                 so something above it is swallowing touch and
 *                                 this is a layering problem, not a canvas one.
 *
 * Counters only -- no behaviour changes -- so shipping this cannot itself
 * alter what is being measured. Delete it once the cause is known.
 */

export interface TouchProbeCounts {
  domTouch: number;
  fabricDown: number;
  fabricMove: number;
  fabricUp: number;
}

const counts: TouchProbeCounts = {
  domTouch: 0,
  fabricDown: 0,
  fabricMove: 0,
  fabricUp: 0,
};

export function noteDomTouch(): void {
  counts.domTouch++;
}
export function noteFabricDown(): void {
  counts.fabricDown++;
}
export function noteFabricMove(): void {
  counts.fabricMove++;
}
export function noteFabricUp(): void {
  counts.fabricUp++;
}

/** A compact single-row summary for the on-device readout. */
export function touchProbeSummary(): string {
  return `dom:${counts.domTouch} down:${counts.fabricDown} move:${counts.fabricMove} up:${counts.fabricUp}`;
}

export function touchProbeCounts(): TouchProbeCounts {
  return { ...counts };
}

/** Test seam. */
export function resetTouchProbe(): void {
  counts.domTouch = 0;
  counts.fabricDown = 0;
  counts.fabricMove = 0;
  counts.fabricUp = 0;
}
